import React from 'react';
import { Box } from '@mui/material';

import { Typography } from '@occc-app/design-system';
import { FormInput } from '@occc-app/Offer/components/FormInput';
import { numberTOOrdinal } from '@occc-app/lib/format';

import { ProductGroupSelectEnhancedContainer as ProductGroupSelect } from './ProductGroupSelectEnhanced/ProductGroupSelectEnhancedContainer';

import {
  greaterThanZeroValidation,
  numberValidation,
  requiredValidation,
  withFormEventValidation,
} from '../../validatableComponent';
import { FormRadio } from '../../FormRadio';
import { FormLabel } from '../../FormLabel';
import { State, PerTaggedGroupPurchasedGroup } from '../redux';
import { Column } from '../../Column';
import { Row } from '../../Row';

const CountThresholdFormInput = withFormEventValidation(
  FormInput,
  requiredValidation,
  numberValidation,
  greaterThanZeroValidation,
);

type ItemGroup = PerTaggedGroupPurchasedGroup & {
  idx: number;
  createOfferMode: boolean;
  editMode: boolean;
  fieldDisabled: (field: Fields) => boolean;
};
const ItemGroup: React.FC<ItemGroup> = (props) => {
  const { idx, countPer, countThreshold, editMode, fieldDisabled, summationMode, tags } = props;

  return (
    <Box>
      <Row>
        <Column>
          <Typography component='p' variant='subtitle1'>
            {numberTOOrdinal(idx)} Product Definition
          </Typography>
        </Column>
      </Row>
      <Row>
        <Column>
          <FormLabel>Summation Mode</FormLabel>
          <FormRadio
            name={`summation-mode-${idx}`}
            id={`summation-mode-include-${idx}`}
            label='Include'
            disabled={fieldDisabled('summationMode')}
            value='include'
            checked={summationMode === 'include'}
          />
          <FormRadio
            name={`summation-mode-${idx}`}
            id={`summation-mode-exclude-${idx}`}
            label='Exclude'
            disabled={fieldDisabled('summationMode')}
            value='exclude'
            checked={summationMode === 'exclude'}
          />
        </Column>
      </Row>
      <Row>
        <Column>
          <FormLabel>Count Products By</FormLabel>
          <FormRadio
            name={`count-per-${idx}`}
            id={`count-per-quantity-${idx}`}
            label='Quantity'
            disabled={fieldDisabled('countPer')}
            value='quantity'
            checked={countPer === 'quantity'}
          />
          <FormRadio
            name={`count-per-${idx}`}
            id={`count-per-mount-amount-${idx}`}
            label='Dollars Spent'
            disabled={fieldDisabled('countPer')}
            value='money_amount'
            checked={countPer === 'money_amount'}
          />
        </Column>
      </Row>
      <Row>
        <Column>
          <CountThresholdFormInput
            label='Count Threshold'
            readonly={fieldDisabled('countThreshold')}
            type='text'
            value={countThreshold}
            required={true}
          />
        </Column>
      </Row>
      <Row>
        <Column mb={4}>
          <ProductGroupSelect
            editMode={editMode}
            onChange={() => {}}
            readonly={fieldDisabled('tags')}
            selectedProductGroups={tags}
          />
        </Column>
      </Row>
    </Box>
  );
};

type ComponentState = State['emConfiguration']['perTaggedGroupPurchased'];
type Fields = keyof ComponentState['groups'][0];
export type Props = ComponentState & {
  createOfferMode: boolean;
  editMode: boolean;
  fieldDisabled: (field: Fields) => boolean;
};

const PerTaggedGroupPurchasedConfiguration: React.FC<Props> = (props) => {
  const { createOfferMode, editMode, fieldDisabled, groups } = props;

  const renderGroup = (group: PerTaggedGroupPurchasedGroup, idx: number) => {
    return (
      <Row key={idx}>
        <Column p={2}>
          <ItemGroup
            idx={idx + 1}
            createOfferMode={createOfferMode}
            editMode={editMode}
            fieldDisabled={fieldDisabled}
            {...group}
          />
        </Column>
      </Row>
    );
  };

  // State does not care about the order of these groups when adding/removing/updating
  // But the UI wants to preserve sorting order regardless of which was last updated
  const itemGroups = groups
    .filter((group) => !group.removed)
    .sort((groupA, groupB) => groupA.groupIndex - groupB.groupIndex)
    .map(renderGroup);
  return <Box mb={4}>{itemGroups}</Box>;
};

export { PerTaggedGroupPurchasedConfiguration };
